/**
 * books 域的前端 RBAC 门控 hook —— 书籍页与其对话框共用的**单一权威**。
 *
 * 浏览面来源展示由 `useCanViewBookSource` 统一门控；建书及显式来源维护面复用
 * `useCanWriteBooks` 的 operator+ 权限，避免把浏览限制误用于运营维护。
 * 三个 hook 同源同体例，一并放这里，不按消费方个数拆。
 *
 * **前端只做 UX 门控，后端 `requireRole` 才是权威**：这里放行不代表能写成功，
 * 这里禁用也只是把失败提前到点击之前、不构成安全边界。
 */

import { useAuthStore } from '~/stores/auth-store';

/**
 * books 一般写动作权限（创建书 / 编辑 / 加语言 / 章节创建发布 / 分类标签创建编辑）——
 * server 对应路由 `requireRole('root','admin','operator')`；viewer 只读。
 */
export function useCanWriteBooks(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

/**
 * books 上下架 / App 级覆盖写权限——server `POST /books/:id/status` 与 `/overrides` 限 root/admin
 * （operator 不可）。
 */
export function useCanChangeBookStatus(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}

/**
 * 采集来源浏览权限 —— **仅 root 角色**，覆盖列表「来源」列、来源筛选和详情「外部采集信息」区块。
 * admin / operator / viewer 默认不展示来源渠道与外部源 id；建书及显式来源维护表单
 * 是 operator+ 有意例外，使用 `useCanWriteBooks`（ADM-BOK-S004「采集来源展示口径」）。
 *
 * **当前只到展示层，不是响应裁剪**：后端仍按契约对全部角色下发 `source` / `source_provider` /
 * `source_external_id`。要做到「非 root 真的取不到」需按角色裁剪响应体，而 `source` 在 `NovelSchema`
 * 是必返字段，当前契约有意保留响应、由前端按面收敛。**必须服务端裁的是 CSV 导出**
 * （文件落盘后前端门控管不到内容）。
 */
export function useCanViewBookSource(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root';
}
